import { useState, useRef, useEffect, forwardRef } from 'react'
import { Input } from '../ui/input'
import { usePieceAutocompleteWithFallback } from '../hooks/usePieceAutocompleteWithFallback'
import type { Piece } from '../hooks/usePieceAutocomplete'
import type { GetComposerWorksResponse } from '@brent/shared/types'

interface PieceAutocompleteInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange'
> {
  value: string
  onChange: (value: string) => void
  onPieceSelect?: (piece: Piece | null) => void
  composerId: string | null
  isComposerFreetext?: boolean // If true, disable autocomplete (can't query Wikidata)
  searchFn?: (composerId: string, limit: number) => Promise<GetComposerWorksResponse>
}

/**
 * Autocomplete input for piece titles with client-side fuzzy search
 * Requires a composer to be selected first (composerId)
 */
export const PieceAutocompleteInput = forwardRef<HTMLInputElement, PieceAutocompleteInputProps>(
  (
    {
      value,
      onChange,
      onPieceSelect,
      composerId,
      isComposerFreetext = false,
      searchFn,
      onBlur,
      disabled = false,
      placeholder = 'e.g., Symphony No. 5',
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const internalRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Use either the forwarded ref or our internal ref
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef

    // Get autocomplete results with Lambda fallback (disabled if composer is freetext)
    const {
      pieces: suggestions,
      isLoading,
      error,
      source,
    } = usePieceAutocompleteWithFallback(isComposerFreetext ? null : composerId, value, searchFn)

    // Show dropdown when focused and (has suggestions OR loading)
    // Don't show if composer is freetext (can't query Wikidata without composer ID)
    const showDropdown =
      !isComposerFreetext && isFocused && value.length >= 2 && (suggestions.length > 0 || isLoading)

    // Show "Add anyway" option whenever user has typed enough (even while loading)
    // Only show if composer is from Wikidata (not freetext)
    const showFreetextOption = !isComposerFreetext && isFocused && value.length >= 2 && composerId

    // Handle selecting a piece from the dropdown
    const selectPiece = (piece: Piece) => {
      // Format: "Symphony No. 40 (K. 550)"
      const formattedTitle = piece.catalogNumber
        ? `${piece.title} (${piece.catalogNumber})`
        : piece.title

      onChange(formattedTitle)
      onPieceSelect?.(piece)
      setIsFocused(false)
      setSelectedIndex(-1)
      inputRef.current?.blur()
    }

    // Handle adding as freetext entry
    const addAsFreetext = () => {
      const freetextPiece: Piece = {
        id: 'FREETEXT', // Special marker for freetext entries (no real Wikidata ID)
        title: value,
        composerId: composerId || '',
        source: 'freetext',
      }
      onPieceSelect?.(freetextPiece)
      setIsFocused(false)
      inputRef.current?.blur()
    }

    // Track when the input value changes to detect clearing
    const handleChange = (newValue: string) => {
      onChange(newValue)
      // If value is cleared, notify parent
      if (newValue === '' && onPieceSelect) {
        onPieceSelect(null)
      }
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!showDropdown) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && suggestions[selectedIndex]) {
            selectPiece(suggestions[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsFocused(false)
          setSelectedIndex(-1)
          break
      }
    }

    // Scroll selected item into view
    useEffect(() => {
      if (selectedIndex >= 0 && dropdownRef.current) {
        const selectedItem = dropdownRef.current.children[selectedIndex] as HTMLElement
        selectedItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }, [selectedIndex])

    // Reset selected index when suggestions change
    useEffect(() => {
      setSelectedIndex(-1)
    }, [suggestions])

    return (
      <div className="relative">
        <Input
          {...props}
          ref={ref || internalRef}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            // Delay blur to allow click events on dropdown items
            setTimeout(() => {
              setIsFocused(false)
              setSelectedIndex(-1)
              onBlur?.(e)
            }, 200)
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled || (!composerId && !isComposerFreetext)}
          placeholder={
            isComposerFreetext
              ? 'Enter piece title manually'
              : composerId
                ? placeholder
                : 'Select a composer first'
          }
          inputMode="search"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? 'piece-suggestions' : undefined}
        />

        {isComposerFreetext && isFocused && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-yellow-50 p-3 text-sm text-yellow-800 shadow-md">
            You selected a custom composer. Autocomplete is disabled. Please type the piece title
            manually.
          </div>
        )}

        {(showDropdown || showFreetextOption) && (
          <div
            id="piece-suggestions"
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
            role="listbox"
          >
            {isLoading && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Searching Wikidata for works...
              </div>
            )}

            {error && !isLoading && (
              <div className="px-3 py-2 text-sm text-destructive">
                Failed to search: {error.message}
              </div>
            )}

            {!isLoading &&
              suggestions.map((piece, index) => (
                <div
                  key={piece.id}
                  role="option"
                  aria-selected={selectedIndex === index}
                  className={`
                cursor-pointer px-3 py-2 text-sm
                ${selectedIndex === index ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
              `}
                  onClick={() => selectPiece(piece)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{piece.title}</div>
                    {piece.source === 'freetext' && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        Custom Entry
                      </span>
                    )}
                  </div>
                  {(piece.catalogNumber || piece.year) && (
                    <div className="text-xs text-muted-foreground">
                      {[piece.catalogNumber, piece.year].filter(Boolean).join(' • ')}
                    </div>
                  )}
                  {source === 'lambda' && (
                    <div className="text-xs text-muted-foreground italic">from Wikidata</div>
                  )}
                </div>
              ))}

            {showFreetextOption && (
              <div
                role="option"
                className="cursor-pointer border-t px-3 py-2 text-sm hover:bg-accent/50"
                onClick={addAsFreetext}
              >
                <div className="font-medium text-muted-foreground">
                  Can't find it? Add "{value}" anyway
                </div>
                <div className="text-xs text-muted-foreground">
                  This will be saved as a custom entry
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
)

PieceAutocompleteInput.displayName = 'PieceAutocompleteInput'
